import React, { useEffect } from "react";
import { useForm, Controller, useWatch } from "react-hook-form";
import { Select, TextInput, Group, Button } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useTranslation } from "react-i18next";
import { ACTIVITY_SELECT_ITEMS, MENU_SELECT_ITEMS, ROLE_SELECT_ITEMS } from "../constants";
import {
    setPage,
    useSelectPageActivityLog,
    useSelectTakeActivityLog,
} from "../../../redux/features/activityLog/activityLogSlice";
import useOnFilter from "./useOnFilter";
import { useDispatch } from "react-redux";
import IconSearch from "@tabler/icons-react/dist/esm/icons/IconSearch.mjs";
import IconCalendarWeek from "@tabler/icons-react/dist/esm/icons/IconCalendarWeek.mjs";
import classes from "./activityLogFilters.module.scss";

const ActivityLogFilters = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const page = useSelectPageActivityLog();
    const take = useSelectTakeActivityLog();

    const { control, register, unregister, handleSubmit, getValues } = useForm();
    const { onFilter } = useOnFilter();

    const selectedType = useWatch({
        control,
        name: "type",
    });

    useEffect(() => {
        unregister(["activity", "date", "userName", "role", "menu"]);
    }, [selectedType, unregister]);

    useEffect(() => {
        handleFetchData();
    }, [page, take]); // eslint-disable-line react-hooks/exhaustive-deps

    function handleFetchData() {
        const formData = getValues();
        const { type, ...rest } = formData;
        onFilter({ page, take, formData: rest });
    }

    const onSubmit = (data) => {
        const { type, ...rest } = data;
        dispatch(setPage(0));
        onFilter({ page, take, formData: rest });
    };

    const TYPE_SELECT_ITEMS = [
        { value: "activity", label: t("activity.activity") },
        { value: "date", label: t("activity.date") },
        { value: "userName", label: t("activity.user") },
        { value: "role", label: t("activity.role") },
        { value: "menu", label: t("activity.menu") },
    ];

    return (
        <form className={classes["filters-box"]} onSubmit={handleSubmit(onSubmit)}>
            <Group align="flex-start">
                <Controller
                    name="type"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                        <Select
                            clearable
                            {...field}
                            placeholder="Filter by"
                            data={TYPE_SELECT_ITEMS}
                        />
                    )}
                />

                {selectedType === "activity" && (
                    <Controller
                        name="activity"
                        control={control}
                        defaultValue={null}
                        render={({ field }) => (
                            <Select
                                {...field}
                                placeholder={t("activity.activity")}
                                data={ACTIVITY_SELECT_ITEMS}
                            />
                        )}
                    />
                )}

                {selectedType === "date" && (
                    <Controller
                        name="date"
                        control={control}
                        defaultValue={null}
                        render={({ field }) => (
                            <DatePickerInput w={200} rightSection={<IconCalendarWeek />} placeholder={t("activity.date")} {...field} />
                        )}
                    />
                )}

                {selectedType === "userName" && (
                    <Controller
                        name="userName"
                        control={control}
                        defaultValue={null}
                        render={({ field }) => (
                            <TextInput placeholder={t("activity.user")} {...field} />
                        )}
                    />
                )}

                {selectedType === "role" && (
                    <Controller
                        name="role"
                        control={control}
                        defaultValue={null}
                        render={({ field }) => (
                            <Select
                                data={ROLE_SELECT_ITEMS}
                                placeholder={t("activity.role")}
                                {...field}
                            />
                        )}
                    />
                )}

                {selectedType === "menu" && (
                    <Controller
                        name="menu"
                        control={control}
                        defaultValue={null}
                        render={({ field }) => (
                            <Select
                                data={MENU_SELECT_ITEMS}
                                placeholder={t("activity.menu")}
                                {...field}
                                defaultValue={null}
                            />
                        )}
                    />
                )}

                <TextInput rightSection={<IconSearch />} placeholder="Search" {...register("search")} clearable />

                <Button type="submit">Submit</Button>
            </Group>
        </form>
    );
};

export default ActivityLogFilters;
